import { useContext, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  BoxFull,
  Button,
  Dropdown,
  TransactionalSubheader,
} from "iuvity-ui-components";
import { CustomInput, FormSkeleton, Header } from "../../components";
import { useWidgets } from "../../hooks/useWidgets";
import {
} from "../../constants";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { useVersions } from "../../hooks/useVersions";
import { TranslationsSchema } from "../../validations/schemas";
import { ErrorMessage } from "@hookform/error-message";
import { AppContext } from "../../context/index";
import { useConfirmationMessages } from "../../hooks/useConfirmationMessages";

interface NewConfirmationMessagesProps {
  update?: boolean;
}

const formBlankValues = {
  widgets: [],
  action: '',
  ospProcess: '',
  idtag: '',
  ibmessage: '',
  language: '',
  releaseversion: "",
};

export const NewConfirmationMessagesPage: React.FC<NewConfirmationMessagesProps> = ({
  update = false,
}) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, selectedConfirmationMessagesLabel },
    setAppState,
  } = useContext(AppContext);

  const {
    control,
    getValues,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: update
      ? {
        widgets: selectedConfirmationMessagesLabel?.widgets.map((widget: any) => ({
          name: widget,
          code: widget,
        })) || [],
          ospProcess: selectedConfirmationMessagesLabel?.ospProcess,
          action: selectedConfirmationMessagesLabel?.action,
          idtag: selectedConfirmationMessagesLabel?.idtag,
          ibmessage: selectedConfirmationMessagesLabel?.ibmessage || "",
          language: selectedConfirmationMessagesLabel?.language,
          releaseversion: selectedConfirmationMessagesLabel?.releaseversion,
          updatedby: user?.name,
        }
      : {
          ...formBlankValues,
          createdby: user?.name,
        },
  });

  const { confirmationMessagesAction } = useConfirmationMessages("ACTIONS");
  const { data: versionsData } = useVersions("DATA");
  const { data: widgets } = useWidgets("DATA");
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const onReturn = () => {
    setAppState((prev) => ({
      ...prev,
      selectedLabel: null,
    }));
    navigate(-1);
    window.scrollTo(0, 0);
  };

  const onAction = async () => {
    const formValues = getValues();
    const actionResult = await confirmationMessagesAction(update ? "UPDATE" : "CREATE", [
      {
        formValues
      },
    ]);
    if (actionResult === "SUCCESS") {
      navigate("/confirmationmessages");
      window.scrollTo(0, 0);
    }
  };

  const handleModalProps = (actionType: "action" | "cancel") => {
    switch (actionType) {
      case "action":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t(`ConfirmationMessages.${update ? "save" : "create"}ModalTitle`),
          content: t(`ConfirmationMessages.${update ? "save" : "create"}ModalContent`),
          onConfirm: onAction,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("ConfirmationMessages.cancelModalTitle"),
          content: t("ConfirmationMessages.cancelModalContent"),
          onConfirm: onReturn,
        }));
      default:
        return;
    }
  };

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {update ? t("ConfirmationMessages.editTitle") : t("ConfirmationMessages.newTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("ConfirmationMessages.confirmationMessagesTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("ConfirmationMessages.confirmationMessagesTitle"),
              command: () => navigate(-1),
            },
            {
              label: update
                ? t("ConfirmationMessages.editTitle")
                : t("ConfirmationMessages.newTitle"),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      {widgets && versionsData ? (
        <>
          <BoxFull
            className="flex flex-column align-items-center justify-content-center my-4"
            style={{ background: "transparent" }}
          >
            <div className="form-container md py-5 px-8">
              <div className="form md">

                <label htmlFor={"input-label"} className="block lb mt-2 mb-2">
                  {t("ConfirmationMessages.functionalityLabel")}
                </label>
                <div className="item-value mb-4">{selectedConfirmationMessagesLabel?.widgets?.map((widget: any) => (
                  <div>{widget}</div>))}
                </div>

                {["ospProcess", "action", "idtag"].map((key: any) => (
                  <CustomInput
                    id={`input-label`}
                    className="rd-only mb-3"
                    name={key}
                    value={getValues(key)}
                    label={t(`ConfirmationMessages.${key}Label`)}
                    placeholder={t(`ConfirmationMessages.${key}PlaceholderLabel`)}
                    type="text"
                    autoComplete="off"
                    readOnly
                  /> 
                ))}
                   

                {["ibmessage"].map((key) => (
                  <div key={key}>
                    <Controller
                      key={key}
                      name={key as any}
                      control={control}
                      render={({ field }) => {
                        return (
                          <CustomInput
                            id={`input-label-${key}`}
                            label={t(`ConfirmationMessages.${key}Label`)}
                            placeholder={t(
                              `ConfirmationMessages.${key}PlaceholderLabel`
                            )}
                            type="text"
                            autoComplete="off"
                            // @ts-ignore
                            className={`${
                              (errors as any)[key]?.message
                                ? "p-invalid"
                                : "input-md mb-4"
                            }`}
                            {...field}
                          />
                        );
                      }}
                    />
                    <ErrorMessage
                      key={`err-${key}`}
                      errors={errors}
                      name={key}
                      //@ts-ignore
                      render={({ message }) => (
                        <small
                          className="p-error block"
                          style={{
                            marginTop: "0.5rem",
                            marginBottom: "1.5rem",
                          }}
                        >
                          {message}
                        </small>
                      )}
                    />
                  </div>
                ))}

                <CustomInput
                  id={`input-label`}
                  className="rd-only mb-3"
                  name="language"
                  value={getValues('language')}
                  label={t("ConfirmationMessages.languageLabel")}
                  placeholder={t(`ConfirmationMessages.languagePlaceholderLabel`)}
                  type="text"
                  autoComplete="off"
                  readOnly
                />

                <Controller
                  name="releaseversion"
                  control={control}
                  rules={TranslationsSchema(t).releaseversion}
                  render={({ field }) => {
                    return update ? (
                      <CustomInput
                        id={`input-label-version`}
                        label={t(`ConfirmationMessages.releaseversionLabel`)}
                        placeholder={t(`ConfirmationMessages.releaseversionPlaceholderLabel`)}
                        type="text"
                        autoComplete="off"
                        // @ts-ignore
                        className="rd-only input-md mb-4"
                        maxLength={300}
                        {...field}
                        readOnly={true}
                      />
                    ) : (
                      // @ts-ignore
                      <Dropdown
                        className={`${
                          errors.releaseversion?.message ? "p-invalid" : "dropdown mb-4"
                        }`}
                        label={t("ConfirmationMessages.releaseversionLabel")}
                        placeholder={t("ConfirmationMessages.releaseversionPlaceholderLabel")}
                        options={versionsData
                          .map((version: any) => ({
                            label: version.name,
                            value: version.name,
                          }))
                          .sort((a: any, b: any) =>
                            b.label.localeCompare(a.label)
                          )}
                        filter={false}
                        showClear={false}
                        width="35rem"
                        {...field}
                      />
                    );
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name="releaseversion"
                  //@ts-ignore
                  render={({ message }) => (
                    <small
                      className="p-error block"
                      style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
                    >
                      {message}
                    </small>
                  )}
                />
              </div>
            </div>

            <div className="form-btngroup-footer my-5 p-0 gap-6">
              <Button
                variant="p-button-link"
                label={t("ConfirmationMessages.cancelBtnLabel")}
                onClick={isDirty ? () => handleModalProps("cancel") : onReturn}
              />
              <Button
                variant="p-button"
                label={
                  update
                    ? t("ConfirmationMessages.editBtnLabel")
                    : t("ConfirmationMessages.createBtnLabel")
                }
                disabled={update ? !(isValid && isDirty) : !isValid}
                onClick={() => handleModalProps("action")}
              />
            </div>
          </BoxFull>
        </>
      ) : (
        <BoxFull className="dt-c my-4">
          <FormSkeleton length={8} />
        </BoxFull>
      )}

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
